"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { MegaphoneIcon } from "@heroicons/react/24/solid";
import { Text } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import { joinClassNames } from "@utils/helpers";

interface Props {
  slug: string;
  image?: string;
  name: string;
  showNameOnMobile: boolean;
  onClick?: () => void;
}

export default function LandingPageLinkV2({
  slug,
  image,
  name,
  showNameOnMobile,
  onClick,
}: Props) {
  const isShopsite = useIsShopsite();

  return (
    <Link href={slug} className="relative w-full h-fit group" onClick={onClick}>
      {image ? (
        <Image
          src={image.trim()}
          alt={name}
          width={460}
          height={110}
          className={joinClassNames(
            "object-cover w-full aspect-[46/11]",
            isShopsite ? "rounded-xl" : "rounded-[var(--radius-4)]"
          )}
        />
      ) : (
        <div
          className={joinClassNames(
            "flex items-center justify-center w-full aspect-[46/11]",
            isShopsite
              ? "rounded-xl bg-indigo-50"
              : "rounded-[var(--radius-4)] bg-[color:var(--accent-a2)]"
          )}
        >
          <MegaphoneIcon
            className={joinClassNames(
              "w-1/2 h-1/2",
              isShopsite ? "text-indigo-800" : "text-[color:var(--accent-9)]"
            )}
          />
        </div>
      )}
      <div
        className={joinClassNames(
          "max-md:hidden flex gap-1 items-center absolute bottom-2 left-2 py-1 px-2 max-w-[calc(100%-1rem)] group-hover:opacity-100 opacity-0 transition-opacity ease-in-out duration-200",
          isShopsite
            ? "rounded-lg bg-indigo-100 text-indigo-800"
            : "rounded-[var(--radius-3)] bg-[color:var(--accent-3)] text-[color:var(--accent-a11)]"
        )}
      >
        <Text
          size="2"
          weight="medium"
          sharedClasses="truncate"
          shopsiteClasses="text-sm font-medium"
        >
          {name}
        </Text>
        <ArrowRightIcon className="h-4 w-4" />
      </div>
      {showNameOnMobile && (
        <Text
          as="p"
          size="2"
          weight="medium"
          sharedClasses="md:hidden pt-2"
          shopsiteClasses="text-black text-sm font-medium"
        >
          {name}
        </Text>
      )}
    </Link>
  );
}
